import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { MediaType } from 'components/Wizards/schemas/shared/mediaType'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ASPECT_RATIOS, DesignChannel } from 'components/NewFileModal/Types'
import { TimeOfDay, CustomScheduleConfig } from './components/PostTimes/ScheduleSelector/types'
import { DayAndTime } from 'components/Wizards/ContentPlanning/ScheduleContent/components/StartDateAndPostFrequency/StartDateAndPostFrequencyModal'
import dayjs, { Dayjs } from 'dayjs'
import { AlmanacFile } from 'types'
import { Tone } from 'components/VoiceAndTonesSelect/Types'

enum PostingCadence {
  Once = 'Post once per week',
  Twice = 'Post twice per week (T/Th)',
  Thrice = 'Post three times per week (M/W/F)',
  Weekdays = 'Post once per weekday',
  Everyday = 'Post once per day',
  Custom = 'custom',
}

const textChannelForGenerationType = (channel: GenerationType) => {
  if (channel === GenerationType.FacebookPlanning) {
    return ChannelType.FacebookPost
  } else if (channel === GenerationType.TwitterPlanning) {
    return ChannelType.Tweets
  } else if (channel === GenerationType.LinkedInPlanning) {
    return ChannelType.LinkedInPosts
  } else {
    return ChannelType.InstagramPosts
  }
}

const generationTypeForMediaType = (mediaType: MediaType, channel: GenerationType) => {
  if (mediaType === MediaType.Design) {
    if (channel === GenerationType.FacebookPlanning) {
      return DesignChannel.FacebookPost
    } else if (channel === GenerationType.TwitterPlanning) {
      return DesignChannel.TwitterPost
    } else if (channel === GenerationType.LinkedInPlanning) {
      return DesignChannel.LinkedInPost
    } else {
      return DesignChannel.InstagramPost
    }
  } else {
    return textChannelForGenerationType(channel)
  }
}

export const getGenerationTypesForMediaType = (mediaType: MediaType, channel: GenerationType) => {
  const captionsGenerationType = textChannelForGenerationType(channel)
  const generationType = generationTypeForMediaType(mediaType, channel)
  return { captionsGenerationType, generationType }
}

export const getAspectRatioForGenerationType = (generationType: GenerationType) => {
  if (generationType === GenerationType.FacebookPlanning) {
    return ASPECT_RATIOS['facebook-post']
  } else if (generationType === GenerationType.TwitterPlanning) {
    return ASPECT_RATIOS['twitter-post']
  } else if (generationType === GenerationType.LinkedInPlanning) {
    return ASPECT_RATIOS['linkedin-post']
  } else {
    return ASPECT_RATIOS['instagram-square-post']
  }
}

// ============================================================================
// SCHEDULING UTILITIES
// ============================================================================

interface Values {
  topic?: string
  caption_length?: string
  count?: number
  filename?: string
  tone?: Array<Tone>
  language?: string
  project?: AlmanacFile
  include_images?: boolean
  brand_kit_id?: number
  width?: number
  height?: number
  start_time?: Date
  post_frequency?: PostingCadence
  post_time?: TimeOfDay
  is_custom_schedule?: boolean
  custom_schedule_data?: CustomScheduleConfig
}

export const DAY_NAME_TO_NUMBER: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
}

export const FREQUENCY_TO_DAYS: Record<PostingCadence, number[]> = {
  [PostingCadence.Once]: [],
  [PostingCadence.Twice]: [2, 4],
  [PostingCadence.Thrice]: [1, 3, 5],
  [PostingCadence.Weekdays]: [1, 2, 3, 4, 5],
  [PostingCadence.Everyday]: [0, 1, 2, 3, 4, 5, 6],
  [PostingCadence.Custom]: [],
}

export const convertCustomScheduleData = (customData: CustomScheduleConfig): DayAndTime[] => {
  const result = customData.selectedDays.map((dayName) => {
    const selection = customData.dayTimeSelections[dayName]
    const day = DAY_NAME_TO_NUMBER[dayName] ?? 1

    if (selection?.timeType === TimeOfDay.Custom && selection.customTime) {
      const { hour, minute, amOrPm } = selection.customTime

      return {
        day,
        time: {
          ofDay: TimeOfDay.Custom,
          at: {
            hour,
            minute,
            amOrPm,
          },
        },
      }
    }

    return { day, time: { ofDay: selection?.timeType || TimeOfDay.Morning } }
  })

  return result
}

export const generateDayAndTimeFromFrequency = (
  frequency: PostingCadence,
  timeOfDay: TimeOfDay,
  startDate: Dayjs,
  customScheduleData?: CustomScheduleConfig,
): DayAndTime[] => {
  let time: { ofDay: TimeOfDay; at?: { hour: number; minute: number; amOrPm: string } } = {
    ofDay: timeOfDay,
  }

  // Handle custom time case for the happy path
  if (timeOfDay === TimeOfDay.Custom && customScheduleData) {
    const customTimeSelection = customScheduleData.dayTimeSelections['all']
    if (customTimeSelection?.customTime) {
      const { hour, minute, amOrPm } = customTimeSelection.customTime
      time = {
        ofDay: TimeOfDay.Custom,
        at: {
          hour,
          minute,
          amOrPm,
        },
      }
    }
  }

  const days = frequency === PostingCadence.Once ? [startDate.day()] : FREQUENCY_TO_DAYS[frequency]

  return days.map((day) => ({ day, time }))
}

export const generateSchedulingData = (values: Values): DayAndTime[] => {
  const startDate = values.start_time ? dayjs(values.start_time) : dayjs().add(1, 'day')

  if (values.is_custom_schedule && values.custom_schedule_data) {
    return convertCustomScheduleData(values.custom_schedule_data)
  }

  if (values.post_frequency && values.post_time) {
    return generateDayAndTimeFromFrequency(
      values.post_frequency,
      values.post_time,
      startDate,
      values.custom_schedule_data,
    )
  }

  return [{ day: startDate.day(), time: { ofDay: TimeOfDay.Morning } }]
}
